'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { campaignTag } from '@/lib/analytics/campaignTag';
import { usePathname } from 'next/navigation';
import { classifyTrafficSource } from '@/lib/analytics/trafficSource';

export default function AnalyticsTracker() {
  const { data: session } = useSession();
  /**
   * 🚨 WITHOUT THIS, REMOVING THE ONCE-PER-DAY CAP WOULD HAVE CHANGED NOTHING. This component is
   * mounted in the root layout, which does NOT remount on an App Router client navigation, and the
   * effect's deps were `[username, id]` - both stable for a whole visit. So it fired once per HARD
   * page load and never on an in-app navigation, whoever you were. Lifting the cap on a tracker
   * that cannot fire a second time is the "it typechecks, therefore it works" failure this project
   * keeps paying for, so the two changes only make sense together.
   *
   * ⚠ THIS CHANGES THE SIGNED-IN DATASET TOO, on purpose. Signed-in views were undercounted by the
   * same mechanism; several register findings read page_views for signed-in users and their
   * absolute counts are not comparable across the deploy date. Rates and shares are.
   */
  const pathname = usePathname();
  /** Guards the double-invoke in React strict mode and any re-render that does not change the path.
   *  Deliberately a ref and not state: writing it must never itself trigger a render. */
  const lastSent = useRef<string | null>(null);

  /**
   * DWELL / EXIT CAPTURE (2026-09-19). The two track routes now return the inserted page_views id;
   * we hold the OPEN view here and beacon its elapsed seconds to /api/analytics/page-duration when the
   * user navigates away (effect cleanup) or hides/closes the tab (pagehide/visibilitychange). That fills
   * the previously-dead view_duration column, so "how long on a page" and "which page they left from"
   * (the last view in a session with a duration) become answerable. flushDwell is idempotent per view.
   */
  // id is the page_views UUID (opaque string; the column is a uuid, not the serial the schema claims).
  const currentView = useRef<{ id: string; startedAt: number; flushed: boolean } | null>(null);
  const flushDwell = useCallback(() => {
    const v = currentView.current;
    if (!v || v.flushed || !v.id) return;
    v.flushed = true;
    const duration = Math.round((Date.now() - v.startedAt) / 1000);
    if (duration < 1) return;
    try {
      navigator.sendBeacon(
        '/api/analytics/page-duration',
        new Blob([JSON.stringify({ viewId: v.id, duration })], { type: 'application/json' }),
      );
    } catch {
      // best effort - a lost dwell beacon must never affect the page
    }
  }, []);

  // Global exit listeners, mounted once. pagehide covers tab close / external navigation; the hidden
  // branch of visibilitychange covers mobile app-switch and background, where pagehide can be skipped.
  useEffect(() => {
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushDwell(); };
    window.addEventListener('pagehide', flushDwell);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushDwell);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flushDwell]);

  useEffect(() => {
    const trackVisit = async () => {
      try {
        // Ensure we're on the client side
        if (typeof window === 'undefined') return;

        // Check if we're in a logout state - skip tracking if so
        const urlParams = new URLSearchParams(window.location.search);
        const isLogoutPage = window.location.pathname === '/auth' && urlParams.get('logout') === 'true';
        
        if (isLogoutPage) {
          console.log('[DBG] [AnalyticsTracker] Logout page detected, skipping tracking');
          return;
        }

        // ⚠ KEYED ON PATH **AND** SIGNED-IN IDENTITY, not path alone: signing in mid-visit must
        // still record, or a user's first view as themselves is lost. Re-visiting a path later in
        // the same visit is a genuine second view and is NOT suppressed - only an immediate repeat
        // of the same key is, which is exactly what strict mode's double invoke produces.
        const sendKey = `${window.location.pathname}|${session?.user?.id ?? 'anon'}`;
        if (lastSent.current === sendKey) return;
        lastSent.current = sendKey;

        // Get or create session ID
        let sessionId = localStorage.getItem('analytics-session-id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('analytics-session-id', sessionId);
        }

        // Extract traffic source data
        const referrer = document.referrer;

        // 🚨 SHARED CLASSIFIER (2026-08-16). The if/else chain that lived here matched `twitter.com`
        // and `x.com` but NOT `t.co`, the domain X actually puts in document.referrer for a click on
        // a post - so every X visit was filed as `other` and the channel read as dead. See
        // src/lib/analytics/trafficSource.ts.
        // UTM parameters override the referrer (unchanged behaviour, just expressed as one binding).
        const utmSource = urlParams.get('utm_source');
        const trafficSource = utmSource || classifyTrafficSource(referrer);

        // If user is logged in AND we have a valid session, track user activity
        if (session?.user?.username && session?.user?.id) {
          console.log('[DBG] [AnalyticsTracker] Tracking user activity for:', session.user.username);
          
          try {
            const response1 = await fetch('/api/analytics/track-user-activity', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: session.user.username,
                sessionId,
                trafficSource,
                utmMedium: urlParams.get('utm_medium'),
                utmCampaign: campaignTag(urlParams), // keeps ?from= tags (campaignTag.ts)
                referrerUrl: referrer,
                pagePath: window.location.pathname,
                pageTitle: document.title,
                userAgent: navigator.userAgent,
              }),
            });
            
            const result1 = await response1.json();
            console.log('[STATS] [AnalyticsTracker] track-user-activity response:', result1);
            // Open this view for dwell capture (flushed on nav-away / tab-hide).
            if (result1?.viewId) currentView.current = { id: String(result1.viewId), startedAt: Date.now(), flushed: false };

            // Also update user's last activity timestamp
            const response2 = await fetch('/api/analytics/update-user-activity', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: session.user.username,
              }),
            });
            
            const result2 = await response2.json();
            console.log('[CLK] [AnalyticsTracker] update-user-activity response:', result2);
          } catch (error) {
            console.error('[FAIL] [AnalyticsTracker] Error tracking user activity:', error);
          }
        } else {
          console.log('[DBG] [AnalyticsTracker] No user session found, tracking anonymously');
          /**
           * 🚨 THIS BLOCK USED TO MANUFACTURE THE ANSWER TO EVERY QUESTION ASKED OF IT (fixed
           * 2026-08-16, register entry `anonymous-pageviews-capped-at-one-per-day`). Two filters
           * stood here, and both were measured against prod before removing either:
           *
           *   1. `if (lastTracked === today) return` - ONE ROW PER BROWSER PER DAY. An anonymous
           *      visitor could not physically record a second page view, so every anonymous session
           *      read as a bounce BY CONSTRUCTION. Measured: 1.012 rows per anonymous session-day
           *      against 8.013 signed-in, and a 95% "bounce rate" that was an artifact of this line.
           *   2. `if (hasUtm || hasReferrer)` - DIRECT TRAFFIC WAS NEVER RECORDED AT ALL. Measured:
           *      0 of 1,543 anonymous rows in 30 days had an empty referrer and no utm. Not a few.
           *      Zero. Bookmarks, typed URLs and in-app browsers were wholly invisible.
           *
           * ⚠ EXPECT A STEP CHANGE IN VOLUME ON THE DEPLOY DATE. It is instrumentation, not growth.
           * Any before/after read across that date is invalid; compare within one side of it.
           *
           * ⭐ NO NEW KIND OF DATA IS COLLECTED - same fields as before, on the views we were
           * already meant to be recording and already record for signed-in users. Capturing the
           * user agent (which would let us tell a crawler from a person) is deliberately NOT done
           * here: the privacy policy says "usage data" and does not name it, so that is the owner's
           * call, not a thing to slip in beside a bug fix.
           */
          // The `analytics-last-tracked` key that drove filter 1 is gone rather than left writing
          // to a value nothing reads. Stale keys in existing browsers are harmless: no code path
          // looks at it now, and first-view-of-day is recoverable from created_at when it is wanted.
          const visitRes = await fetch('/api/analytics/track-visit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              trafficSource,
              utmMedium: urlParams.get('utm_medium'),
              utmCampaign: campaignTag(urlParams), // keeps ?from= tags (campaignTag.ts)
              referrerUrl: referrer,
              pagePath: window.location.pathname,
            }),
          });
          // Open this view for dwell capture (flushed on nav-away / tab-hide).
          const visitJson = await visitRes.json().catch(() => null);
          if (visitJson?.viewId) currentView.current = { id: String(visitJson.viewId), startedAt: Date.now(), flushed: false };
        }
      } catch (error) {
        // Silent fail - never break user experience
      }
    };

    trackVisit();
    // Cleanup runs on the NEXT path change (SPA nav) or unmount, before the new view opens: flush the
    // view we are leaving so its dwell time is recorded. The pagehide/visibility listeners cover the
    // hard-exit case (tab close) where this cleanup does not run.
    return () => { flushDwell(); };
  }, [session?.user?.username, session?.user?.id, pathname, flushDwell]);

  return null;
}
